import type { DataRefreshLanguagesPayload } from '@/utils/widgetMessageContract';

export interface CachedQueryLike {
  queryKey: readonly unknown[];
  state: { status: string; data: unknown; dataUpdatedAt: number };
}

type KeyPredicate = (key: readonly unknown[]) => boolean;

const isHomeKey: KeyPredicate = (key) => key[0] === 'home';
const isShopKey: KeyPredicate = (key) => key[0] === 'shop';
// `getProfessionalsQuery` shares the prefix but puts `{ include }` at index 3;
// `'any'` is the synthetic client-side barber in getBarberByIdOrRouteQuery.
const isBarberKey: KeyPredicate = (key) =>
  key[0] === 'booking' &&
  key[1] === 'professionals' &&
  typeof key[3] === 'string' &&
  key[3] !== 'any';

const readString = (value: unknown, path: readonly string[]): string | null => {
  let current: unknown = value;
  for (const segment of path) {
    if (typeof current !== 'object' || current === null) return null;
    current = (current as Record<string, unknown>)[segment];
  }
  return typeof current === 'string' && current !== '' ? current : null;
};

const findLanguage = (
  queries: ReadonlyArray<CachedQueryLike>,
  matches: KeyPredicate,
  paths: ReadonlyArray<readonly string[]>,
): string | null => {
  const candidates = queries
    .filter(
      (query) => query.state.status === 'success' && matches(query.queryKey),
    )
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);

  for (const candidate of candidates) {
    for (const path of paths) {
      const language = readString(candidate.state.data, path);
      if (language) return language;
    }
  }
  return null;
};

export const buildDataRefreshPayload = (
  queries: ReadonlyArray<CachedQueryLike>,
): DataRefreshLanguagesPayload => {
  const brandLanguage =
    findLanguage(queries, isHomeKey, [
      ['brand', 'settings', 'defaultLanguage'],
    ]) ??
    findLanguage(queries, isShopKey, [
      ['brand', 'settings', 'defaultLanguage'],
    ]);
  const shopLanguage = findLanguage(queries, isShopKey, [
    ['settings', 'defaultLanguage'],
  ]);
  const barberLanguage = findLanguage(queries, isBarberKey, [
    ['barber', 'defaultLanguage'],
    ['defaultLanguage'],
  ]);

  return {
    brand: brandLanguage
      ? { settings: { defaultLanguage: brandLanguage } }
      : null,
    shop: shopLanguage ? { settings: { defaultLanguage: shopLanguage } } : null,
    barber: barberLanguage ? { defaultLanguage: barberLanguage } : null,
  };
};
